import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { Rocket, Target, BarChart, Zap, CheckCircle, ArrowUpRight } from 'lucide-react';
import { Card } from '../../components/ui/Card';

interface AgencyDemoProps {
  prospect: Prospect;
}

export const AgencyDemo: React.FC<AgencyDemoProps> = ({ prospect }) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#060606] text-slate-300 font-sans selection:bg-purple-500/30">
      {/* Navbar Moderna */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#060606]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Rocket className="text-white w-5 h-5" />
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight">{prospect.trade_name} Agency</h1>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-400">
            <a href="#cases" className="hover:text-white transition-colors">Cases de Sucesso</a>
            <a href="#metodologia" className="hover:text-white transition-colors">Metodologia</a>
          </nav>
          <button className="bg-white text-black px-6 py-2.5 rounded-full font-bold text-sm hover:bg-slate-200 transition-transform hover:scale-105">
            Agendar Sessão Estratégica
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 relative overflow-hidden flex items-center min-h-[90vh]">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[120px] -z-10 mix-blend-screen pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-indigo-600/20 rounded-full blur-[100px] -z-10 mix-blend-screen pointer-events-none" />
        
        <div className="max-w-7xl mx-auto text-center z-10 w-full animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-semibold mb-8">
            <Zap className="w-4 h-4" />
            <span>Aceleradora de Vendas & Lançamentos</span>
          </div>
          <h2 className="text-5xl lg:text-7xl font-bold text-white tracking-tighter leading-[1.1] max-w-4xl mx-auto mb-8">
            Escale seus resultados com <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400">
              marketing de performance.
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed mb-12">
            Nós não vendemos likes, vendemos conversão. 
            Construímos funis validados, tráfego escalável e criativos que transformam cliques em clientes recorrentes.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <button className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white px-8 py-4 rounded-full font-bold transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25">
              Receber Diagnóstico Gratuito
              <ArrowUpRight className="w-5 h-5" />
            </button>
            <button className="w-full sm:w-auto bg-transparent border border-white/20 text-white px-8 py-4 rounded-full font-bold hover:bg-white/5 transition-colors flex items-center justify-center gap-2">
              Ver Cases de Sucesso
            </button>
          </div>
          
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap justify-center gap-12 opacity-60">
             {/* Fake logos */}
             <div className="text-2xl font-black tracking-widest text-slate-500">FORBES</div>
             <div className="text-2xl font-black tracking-widest text-slate-500">TECHCRUNCH</div>
             <div className="text-2xl font-black tracking-widest text-slate-500">EXAME</div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 px-6" id="metodologia">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-20 reveal-on-scroll">
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6 tracking-tight">O Fim das Métricas de Vaidade</h2>
            <p className="text-slate-400 text-lg">Nosso foco é exclusivamente no seu ROI. Desenvolvemos estratégias robustas para dominar seu nicho de mercado.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-[#111] border-white/5 p-8 rounded-3xl hover:-translate-y-2 transition-transform duration-500 group reveal-on-scroll reveal-delay-100">
              <div className="w-14 h-14 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-6 text-purple-400 group-hover:scale-110 transition-transform">
                <Target className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Tráfego Pago (Growth)</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-purple-500 mt-0.5" /> Meta Ads & Google Ads Avançado</li>
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-purple-500 mt-0.5" /> Remarketing Omnichannel</li>
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-purple-500 mt-0.5" /> Otimização por IA (CPA/ROAS)</li>
              </ul>
            </Card>
            
            <Card className="bg-[#111] border-white/5 p-8 rounded-3xl hover:-translate-y-2 transition-transform duration-500 group reveal-on-scroll reveal-delay-200">
              <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 text-blue-400 group-hover:scale-110 transition-transform">
                <Rocket className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Lançamentos</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-blue-500 mt-0.5" /> Fórmulas de Lançamento (Semente/Interno)</li>
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-blue-500 mt-0.5" /> Gestão de Comunidades (WhatsApp/Discord)</li>
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-blue-500 mt-0.5" /> Copwriting Persuasivo (VSL/Webinário)</li>
              </ul>
            </Card>
            
            <Card className="bg-[#111] border-white/5 p-8 rounded-3xl hover:-translate-y-2 transition-transform duration-500 group reveal-on-scroll reveal-delay-300">
              <div className="w-14 h-14 bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-6 text-indigo-400 group-hover:scale-110 transition-transform">
                <BarChart className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">CRO & Landing Pages</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5" /> Design Orientado à Conversão</li>
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5" /> Testes A/B Multivariáveis</li>
                <li className="flex items-start gap-2 text-slate-400 text-sm"><CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5" /> Carregamento Ultrarrápido (Vite/Edge)</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer Minimalista */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Rocket className="text-purple-500 w-6 h-6" />
            <span className="text-white font-bold tracking-tight">{prospect.trade_name} Agency</span>
          </div>
          <div className="text-sm text-slate-500 font-medium">
            &copy; {new Date().getFullYear()} YouMachine. High-end Marketing Demo.
          </div>
        </div>
      </footer>
    </div>
  );
};
